import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import { CallApiResult } from 'src/api';
import { fetchNextVideo, fetchVideoCategoriesByTopic, fetchVideoLanguages, fetchVideoTopics } from 'src/api/video';
import { enqueueSnackbarAction } from 'src/store/notifications/actions';
import {
    FETCH_NEXT_VIDEO,
    FETCH_VIDEO_CATEGORIES,
    FETCH_VIDEO_LANGUAGES,
    FETCH_VIDEO_TOPICS,
    requestNextVideo,
    requestNextVideoFailure,
    requestNextVideoSuccess,
    requestVideoCategories,
    requestVideoCategoriesFailure,
    requestVideoCategoriesSuccess,
    requestVideoLanguages,
    requestVideoLanguagesFailure,
    requestVideoLanguagesSuccess,
    requestVideoTopics,
    requestVideoTopicsFailure,
    requestVideoTopicsSuccess,
} from './actions';

/* Video Topics */
function* fetchVideoTopicsSaga(action: AnyAction): SagaIterator {
    yield put(requestVideoTopics());

    const { error, response } = yield call(fetchVideoTopics);

    if (error) {
        yield put(requestVideoTopicsFailure(error));
        yield put(
            enqueueSnackbarAction({
                message: error.message || 'Video-Themen konnten nicht geladen werden.',
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        yield put(requestVideoTopicsSuccess(response));
    }
}

export function* generateFetchVideoTopicsWatcher() {
    yield takeLatest(FETCH_VIDEO_TOPICS, fetchVideoTopicsSaga);
}

/* Video Categories by topic (one topic, or several merged in the given topic order) */
function* fetchVideoCategoriesSaga(action: AnyAction): SagaIterator {
    yield put(requestVideoCategories());

    const topics: string[] = action.topics ?? [action.topic];
    const results: CallApiResult[] = yield all(topics.map(topic => call(fetchVideoCategoriesByTopic, topic)));
    const error = results.find(result => result.error)?.error;

    if (error && results.every(result => result.error)) {
        yield put(requestVideoCategoriesFailure(error));
    } else {
        const data = results.flatMap((result, topicRank) =>
            (result.response?.data ?? []).map((category: any) => ({ ...category, topicRank }))
        );
        yield put(requestVideoCategoriesSuccess({ data }));
    }
    if (error) {
        yield put(
            enqueueSnackbarAction({
                message: error.message || 'Videos konnten nicht geladen werden.',
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    }
}

export function* generateFetchVideoCategoriesWatcher() {
    yield takeLatest(FETCH_VIDEO_CATEGORIES, fetchVideoCategoriesSaga);
}

/* Video Languages */
function* fetchVideoLanguagesSaga(action: AnyAction): SagaIterator {
    yield put(requestVideoLanguages());

    const { error, response } = yield call(fetchVideoLanguages, action.topic);

    if (error) {
        yield put(requestVideoLanguagesFailure(error));
        yield put(
            enqueueSnackbarAction({
                message: error.message || 'Videosprachen konnten nicht geladen werden.',
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        yield put(requestVideoLanguagesSuccess(response));
    }
}

export function* generateFetchVideoLanguagesWatcher() {
    yield takeLatest(FETCH_VIDEO_LANGUAGES, fetchVideoLanguagesSaga);
}

/* Next Video */
function* fetchNextVideoSaga(action: AnyAction): SagaIterator {
    yield put(requestNextVideo());

    const { error, response } = yield call(fetchNextVideo);

    if (error) {
        yield put(requestNextVideoFailure(error));
        yield put(
            enqueueSnackbarAction({
                message: error.message || 'Das nächste Video konnte nicht geladen werden.',
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        yield put(requestNextVideoSuccess(response));
    }
}

export function* generateFetchNextVideoWatcher() {
    yield takeLatest(FETCH_NEXT_VIDEO, fetchNextVideoSaga);
}
